import { useEffect, useState } from 'react';
import type { Draft, Task, TaskEvent } from '@jerome2/shared';
import { api } from '../api.ts';
import { ARM_MS, isArmed, nextArm, type Armed } from '../lib/armed.ts';
import { duration, fullDate, money, relative } from '../lib/format.ts';
import { useStore } from '../lib/store.ts';
import { app } from '../state/app.ts';
import { loadTask, taskActions, tasksStore } from '../state/tasks.ts';
import { errorToast, toast } from '../state/toast.ts';
import { Icon, type IconName } from '../ui/Icon.tsx';
import { Markdown } from '../ui/Markdown.tsx';
import { FileLinks } from './FileLinks.tsx';
import { Button, Spinner } from '../ui/primitives.tsx';

// Resultaatkaart boven de mail bij Verwerkt: concept, samenvatting, onderzoek, agendavoorstel,
// ondertekening, vraag, voortgang of fout, met de knoppen Verzenden, Bewerk, Stuur bij, Klaar…
// Op de telefoon smallere marges, een kop die mag wikkelen en een Verzenden van 44 px.

export function cardHeader(task: Task): { title: string; icon: IconName; tone: 'blue' | 'amber' | 'green' | 'red' | 'grey' } {
  switch (task.status) {
    case 'queued':
      return { title: 'In de wachtrij', icon: 'clock', tone: 'grey' };
    case 'running':
      return { title: task.progress ? `Bezig: ${task.progress}` : 'Bezig', icon: 'spinner', tone: 'grey' };
    case 'question':
      return { title: 'Jerome heeft een vraag', icon: 'question', tone: 'amber' };
    case 'failed':
      return { title: 'Mislukt', icon: 'warning', tone: 'red' };
    case 'cancelled':
      return { title: 'Geannuleerd', icon: 'x', tone: 'grey' };
    case 'finished':
      return { title: task.result?.title ?? 'Afgerond', icon: 'check', tone: 'green' };
    case 'done': {
      const type = task.result?.type;
      if (type === 'sign') return { title: task.result?.title ?? 'Ondertekend en gecontroleerd', icon: 'pen', tone: 'green' };
      if (type === 'reply') return { title: task.result?.title ?? 'Concept klaar', icon: 'check', tone: 'blue' };
      if (type === 'agenda') return { title: task.result?.title ?? 'Afspraak voorgesteld', icon: 'calendar', tone: 'blue' };
      return { title: task.result?.title ?? 'Klaar', icon: 'check', tone: 'blue' };
    }
  }
}

export function ResultCard(props: { task: Task; onEdit: (draftId: string) => void; onSteer: () => void; onAnswer: () => void; onFinished: () => void; /** Geen andere open taken meer voor deze mail: Klaar haalt dan ook de vlag weg. */ lastForMail?: boolean | undefined }) {
  const task = useStore(tasksStore, (s) => s.byId[props.task.id] ?? props.task);
  const events = useStore(tasksStore, (s) => s.events[task.id]);
  const narrow = useStore(app, (s) => s.narrow);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [proposalGone, setProposalGone] = useState(false);
  const [armed, setArmed] = useState<Armed | null>(null);
  const header = cardHeader(task);
  const draftId = task.result?.draftId;

  useEffect(() => {
    if (!events) void loadTask(task.id).catch(() => undefined);
  }, [task.id, events]);

  useEffect(() => {
    setDraft(null);
    setDraftError(null);
    if (!draftId || task.status !== 'done') return;
    let cancelled = false;
    api<Draft>(`/api/drafts/${encodeURIComponent(draftId)}`)
      .then((d) => !cancelled && setDraft(d))
      .catch((e: Error) => !cancelled && setDraftError(e.message));
    return () => {
      cancelled = true;
    };
  }, [draftId, task.status, task.updatedAt]);

  const run = async (name: string, fn: () => Promise<void>, ok?: string) => {
    setBusy(name);
    try {
      await fn();
      if (ok) toast(ok, { tone: 'ok' });
    } catch (e) {
      errorToast(e);
    } finally {
      setBusy(null);
    }
  };

  // Versturen vraagt twee klikken (de tweede binnen zes seconden); daarna leert Jerome van de aanpassingen.
  const send = () => {
    const step = nextArm(armed, Date.now());
    setArmed(step.state);
    if (!step.fire) {
      window.setTimeout(() => setArmed(null), ARM_MS);
      return;
    }
    void run('send', async () => {
      if (!draftId) return;
      const r = await api<{ subject: string; learning: boolean }>(`/api/drafts/${encodeURIComponent(draftId)}/send`, { method: 'POST' });
      toast(`Verzonden: ${r.subject}`, { tone: 'ok' });
      if (r.learning) toast('Jerome bekijkt wat je aan zijn concept veranderde en leert daarvan', { tone: 'info' });
      props.onFinished();
    });
  };
  const lastForMail = props.lastForMail !== false;
  const finish = () => run('finish', async () => { await taskActions.finish(task.id); props.onFinished(); }, lastForMail ? 'Klaar, vlag weg' : 'Klaar; de mail blijft staan voor de andere taken');
  const cancel = () => run('cancel', () => taskActions.cancel(task.id), 'Geannuleerd');
  const retry = () => run('retry', () => taskActions.retry(task.id), 'Opnieuw in de wachtrij');
  const approve = () => run('approve', () => taskActions.approve(task.id), 'In je agenda gezet');

  const tones = { blue: 'text-accent', amber: 'text-amber', green: 'text-green', red: 'text-red', grey: 'text-muted' };
  const result = task.result;

  return (
    <section className={`${narrow ? 'mx-3 mt-3' : 'mx-6 mt-5'} rounded-xl border border-line bg-surface shadow-card overflow-hidden`} aria-label="Resultaat van Jerome">
      <header className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-5 min-h-12 py-2 border-b border-line bg-paper/60">
        <Icon name={header.icon} size={17} className={`${tones[header.tone]} ${task.status === 'running' ? 'animate-spin' : ''}`} />
        <span className={`font-medium ${tones[header.tone]}`}>{header.title}</span>
        <span className="ml-auto text-[13px] text-muted">
          {task.endedAt ? `${relative(task.endedAt)}, ${duration(task.startedAt, task.endedAt)}` : task.startedAt ? `gestart ${relative(task.startedAt)}` : `aangemaakt ${relative(task.createdAt)}`}
          {task.costUsd > 0 ? ` · ${money(task.costUsd)}` : ''}
        </span>
      </header>
      <div className="px-5 py-4">
        <p className="text-[13px] text-ink-soft mb-3">
          Jouw instructie: <span className="text-accent">"{task.instruction}"</span>
          {task.answer ? <span className="text-muted"> · {task.source === 'answer' ? 'antwoord' : 'bijgestuurd'}: "{task.answer}"</span> : null}
        </p>

        {task.status === 'question' ? (
          <div className="rounded-lg bg-amber-soft px-4 py-3 text-ink">
            <p className="font-medium text-amber mb-1">Vraag</p>
            <p>{task.question}</p>
            <p className="text-[13px] text-muted mt-2">Typ je antwoord in de balk onderin en druk op Enter; Jerome gaat dan verder.</p>
          </div>
        ) : null}

        {task.status === 'failed' ? <div className="rounded-lg bg-red-soft px-4 py-3 text-red">{task.error ?? 'Er ging iets mis.'}</div> : null}

        {(task.status === 'running' || task.status === 'queued') && events ? <EventLog events={events} /> : null}

        {result && (task.status === 'done' || task.status === 'finished') ? (
          <div className="space-y-4">
            {result.type === 'reply' && draftId ? (
              draft ? (
                <div className="text-[15px] leading-relaxed whitespace-pre-wrap font-sans text-ink">{draft.bodyText.trim() || <span className="text-muted">(leeg concept)</span>}</div>
              ) : draftError ? (
                <p className="text-red text-[13px]">Concept niet te lezen: {draftError}</p>
              ) : (
                <Spinner label="Concept ophalen…" />
              )
            ) : null}
            {result.type !== 'reply' || !draftId ? <Markdown text={result.text} /> : result.text && result.text !== draft?.bodyText ? <p className="text-[13px] text-muted border-t border-line pt-3">{result.text}</p> : null}
            {result.sources?.length ? (
              <div className="text-[13px]">
                <p className="font-medium text-ink-soft mb-1">Bronnen</p>
                <ul className="list-disc pl-5 space-y-0.5">
                  {result.sources.map((s, i) => (
                    <li key={i}>{s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">{s.title}</a> : s.title}{s.ref ? <span className="text-muted"> ({s.ref})</span> : null}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {result.attachments?.length ? <FileLinks files={result.attachments} /> : null}
            {result.sign ? <SignOutcome sign={result.sign} /> : null}
            {result.event && !proposalGone && !task.artifacts['eventCreated'] ? (
              <div className="flex flex-wrap items-center gap-3 rounded-lg bg-amber-soft px-4 py-3">
                <Icon name="calendar" size={17} className="text-amber shrink-0" />
                <span className="flex-1 min-w-[200px] text-[14px]">
                  <span className="font-medium">Afspraak wacht op je akkoord:</span> {result.event.subject}, {fullDate(result.event.start)} tot {fullDate(result.event.end).replace(/^.*?(\d{2}[.:]\d{2})$/, '$1')}{result.event.location ? `, ${result.event.location}` : ''}
                </span>
                <Button variant="primary" size="sm" onClick={approve} busy={busy === 'approve'}>
                  Zet in agenda
                </Button>
                <Button size="sm" onClick={() => setProposalGone(true)}>
                  Niet
                </Button>
              </div>
            ) : null}
            {task.artifacts['eventCreated'] ? <p className="text-[13px] text-green flex items-center gap-1.5"><Icon name="check" size={14} /> Afspraak staat in je agenda.</p> : null}
            {result.notes ? <p className="text-[13px] text-ink-soft border-t border-line pt-3">{result.notes}</p> : null}
          </div>
        ) : null}
      </div>
      <footer className="flex flex-wrap items-center gap-2 px-5 py-3 border-t border-line bg-paper/40">
        {task.status === 'done' && result?.type === 'reply' && draftId ? (
          <>
            <Button variant="primary" onClick={send} busy={busy === 'send'} icon="send" className={`pointer-coarse:h-11 ${isArmed(armed, Date.now()) ? 'ring-2 ring-amber ring-offset-1' : ''}`}>
              {isArmed(armed, Date.now()) ? 'Nu verzenden' : 'Verzenden'}
            </Button>
            <Button onClick={() => props.onEdit(draftId)} icon="pen">
              Bewerk
            </Button>
          </>
        ) : null}
        {task.status !== 'finished' && task.status !== 'cancelled' ? (
          <Button onClick={task.status === 'question' ? props.onAnswer : props.onSteer}>{task.status === 'question' ? 'Antwoord' : 'Stuur bij'}</Button>
        ) : null}
        {task.status === 'done' && result?.type === 'sign' ? (
          <Button variant="danger" onClick={props.onSteer}>
            Er klopt iets niet
          </Button>
        ) : null}
        {task.status === 'failed' ? (
          <Button onClick={retry} busy={busy === 'retry'} icon="refresh">
            Opnieuw
          </Button>
        ) : null}
        {task.status === 'queued' || task.status === 'running' || task.status === 'question' || task.status === 'done' ? (
          <Button variant="ghost" onClick={cancel} busy={busy === 'cancel'}>
            Annuleer
          </Button>
        ) : null}
        {task.status === 'done' || task.status === 'question' || task.status === 'failed' ? (
          <Button variant="green" onClick={finish} busy={busy === 'finish'} icon="check" className="ml-auto" title={lastForMail ? undefined : 'Er lopen nog andere taken voor deze mail; de vlag gaat pas weg bij de laatste'}>
            {lastForMail ? 'Klaar, vlag weg' : 'Klaar'}
          </Button>
        ) : null}
      </footer>
    </section>
  );
}

function EventLog(props: { events: TaskEvent[] }) {
  const items = props.events.filter((e) => e.kind !== 'status' || !e.text.startsWith('Model ')).slice(-8);
  if (!items.length) return null;
  return (
    <ol className="text-[13px] text-ink-soft space-y-1">
      {items.map((e) => (
        <li key={e.id} className="flex items-center gap-2">
          <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${e.kind === 'error' ? 'bg-red' : e.kind === 'tool' ? 'bg-accent' : 'bg-line-strong'}`} />
          <span className="truncate">{e.text}</span>
        </li>
      ))}
    </ol>
  );
}

function SignOutcome(props: { sign: NonNullable<Task['result']>['sign'] }) {
  const s = props.sign;
  if (!s) return null;
  return (
    <div className="rounded-lg border border-green/30 bg-green-soft/50 px-4 py-3 text-[14px]">
      <p className="font-medium text-green flex items-center gap-1.5">
        <Icon name="check" size={15} /> Controle: {s.verdict} ({s.score}/100, {s.rounds} {s.rounds === 1 ? 'ronde' : 'rondes'})
      </p>
      <ul className="mt-2 space-y-1">
        {s.files.map((f) => (
          <li key={f.path} className="flex items-center gap-1.5">
            <Icon name="file" size={13} className="text-muted" /> {f.name} <span className="text-muted">({f.pages} {f.pages === 1 ? 'pagina' : 'pagina\'s'})</span>
          </li>
        ))}
      </ul>
      {s.findings.length ? <ul className="mt-2 list-disc pl-5 text-ink-soft">{s.findings.map((f, i) => <li key={i}>{f}</li>)}</ul> : null}
      {s.files.some((f) => f.thumbnails?.length) ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {s.files.flatMap((f) => (f.thumbnails ?? []).map((p) => (
            <a key={p} href={`/api/sign/thumb?path=${encodeURIComponent(p)}`} target="_blank" rel="noreferrer" className="block rounded border border-line bg-white shadow-sm hover:border-accent" title={p.split('/').pop() ?? p}>
              <img src={`/api/sign/thumb?path=${encodeURIComponent(p)}`} alt={`Getekende pagina ${p.split('/').pop() ?? ''}`} className="h-40 w-auto rounded" loading="lazy" />
            </a>
          )))}
        </div>
      ) : null}
      {s.rulesUsed.length ? <p className="mt-2 text-[12px] text-muted">Leerregels toegepast: {s.rulesUsed.join('; ')}</p> : null}
    </div>
  );
}
